import React from "react";
import api from "../../services/api";
import { photoPickerOpened, photoPickerClosed } from "../../utils/filePickerFocus";
import { validarGradeSemanal } from "./availabilityValidation.mjs";
import '../../components/BelezaUI/styles.css';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  GridLegacy as MuiGrid,
  IconButton,
  Menu,
  Tooltip,
  Paper as MuiPaper,
  Stack as MuiStack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField as MuiTextField,
  MenuItem,
  Typography as MuiTypography,
} from "@mui/material";
import { AddAPhotoOutlined, CameraAltOutlined, DeleteOutline, EditOutlined, VisibilityOutlined } from "@mui/icons-material";

/**
 * Shared UI/Utils do módulo Beleza.
 *
 * Este arquivo é propositalmente compatível com telas antigas e novas.
 * Ele centraliza:
 * - componentes base MUI reexportados;
 * - componentes de página, tabela, diálogo e estatísticas;
 * - moeda, data, status e foto;
 * - helpers de responsividade.
 */

// ============================================================================
// REEXPORTS MUI USADOS NAS TELAS DO MÓDULO
// ============================================================================

export const Button = MuiButton;
export const Grid = MuiGrid;
export const Paper = MuiPaper;
export const Stack = MuiStack;
export const TextField = MuiTextField;
export const Typography = MuiTypography;

// ============================================================================
// MOEDA / VALORES
// ============================================================================

export const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export const formatMoney = (value) => {
  const numero = Number(value || 0);
  return BRL.format(Number.isFinite(numero) ? numero : 0);
};

export const moeda = formatMoney;
export const money = formatMoney;
export const formatCurrency = formatMoney;
export const formatarMoeda = formatMoney;
export const valorMonetario = formatMoney;

export const moedaParaNumero = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  const raw = String(value ?? "").trim().replace(/[^\d,.-]/g, "");
  if (!raw) return 0;
  const comma = raw.lastIndexOf(",");
  const dot = raw.lastIndexOf(".");
  const lastSeparator = Math.max(comma, dot);
  if (lastSeparator < 0) return Number(raw);
  // When both separators occur, whichever is last is the decimal separator.
  // When there is one, three trailing digits normally represent a grouping separator.
  const separator = raw[lastSeparator];
  const lastDigits = raw.length - lastSeparator - 1;
  const distinctSeparators = comma >= 0 && dot >= 0;
  const sameCount = raw.split(separator).length - 1;
  const decimal = distinctSeparators || (sameCount === 1 && lastDigits !== 3) ||
    (sameCount > 1 && lastDigits !== 3);
  const normalized = decimal
    ? raw.slice(0, lastSeparator).replace(/[.,]/g, "") + "." + raw.slice(lastSeparator + 1).replace(/[.,]/g, "")
    : raw.replace(/[.,]/g, "");
  const result = Number(normalized);
  return Number.isFinite(result) ? result : NaN;
};

export const parseMoeda = moedaParaNumero;
export const currencyToNumber = moedaParaNumero;
export const moneyToNumber = moedaParaNumero;
export const valorParaNumero = moedaParaNumero;

export const numeroParaMoedaInput = (value) => {
  const numero = Number(value || 0);
  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatarCampoMoeda = (value) => {
  const numero = moedaParaNumero(value);
  return numeroParaMoedaInput(numero);
};

export const calcularSubtotal = (quantidade, valorUnitario, desconto = 0) => {
  const qtd = Number(quantidade || 0);
  const valor = Number(valorUnitario || 0);
  const desc = Number(desconto || 0);
  return Math.max(0, qtd * valor - desc);
};

export const porcentagem = (value) => {
  const numero = Number(value || 0);
  return `${numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

export const clamp = (value, min, max) => {
  const numero = Number(value || 0);
  return Math.min(Math.max(numero, min), max);
};

// ============================================================================
// DATAS
// ============================================================================

export const formatDateInput = (date) => {
  const d = date ? new Date(date) : new Date();

  if (Number.isNaN(d.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }

  return d.toISOString().slice(0, 10);
};

export const formatDateTimePtBr = (date) => {
  if (!date) return "-";

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDatePtBr = (date) => {
  if (!date) return "-";

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("pt-BR");
};

export const dataPtBr = formatDatePtBr;
export const dataHoraPtBr = formatDateTimePtBr;
export const dataHora = formatDateTimePtBr;
export const formatarData = formatDatePtBr;
export const formatarDataHora = formatDateTimePtBr;

// ============================================================================
// IMAGEM / FOTO
// ============================================================================

export const resolveFotoSrc = (itemOrUrl, fallback = "") => {
  if (!itemOrUrl) return fallback;

  if (typeof itemOrUrl === "string") {
    return itemOrUrl.trim() || fallback;
  }

  return (
    itemOrUrl?.fotoUrlVisualizacao ||
    itemOrUrl?.FotoUrlVisualizacao ||
    itemOrUrl?.urlVisualizacao ||
    itemOrUrl?.UrlVisualizacao ||
    itemOrUrl?.fotoUrl ||
    itemOrUrl?.FotoUrl ||
    itemOrUrl?.imagemUrl ||
    itemOrUrl?.ImagemUrl ||
    itemOrUrl?.urlImagem ||
    itemOrUrl?.UrlImagem ||
    itemOrUrl?.foto ||
    itemOrUrl?.Foto ||
    itemOrUrl?.avatarUrl ||
    itemOrUrl?.AvatarUrl ||
    itemOrUrl?.bannerUrl ||
    itemOrUrl?.BannerUrl ||
    fallback ||
    ""
  );
};

export const getImagem = resolveFotoSrc;
export const getFoto = resolveFotoSrc;
export const getFotoUrl = resolveFotoSrc;
export const resolveImagemSrc = resolveFotoSrc;
export const resolveImageSrc = resolveFotoSrc;

export const getNome = (item) =>
  item?.nome ||
  item?.Nome ||
  item?.clienteNome ||
  item?.ClienteNome ||
  item?.colaboradorNome ||
  item?.ColaboradorNome ||
  item?.produtoNome ||
  item?.ProdutoNome ||
  item?.servicoNome ||
  item?.ServicoNome ||
  item?.nomeFantasia ||
  item?.NomeFantasia ||
  "";

export const iniciais = (nome) => {
  const texto = String(nome || "").trim();
  if (!texto) return "?";

  const partes = texto.split(/\s+/).filter(Boolean);

  if (partes.length === 1) {
    return partes[0].slice(0, 2).toUpperCase();
  }

  return `${partes[0][0] || ""}${partes[partes.length - 1][0] || ""}`.toUpperCase();
};


/** Datas de aniversário são datas civis, não timestamps UTC.
 * Sempre editar em dd/mm/aaaa; nunca atualizar durante a digitação por Date(). */
export const nascimentoParaTexto = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : raw;
};
export const nascimentoParaIso = value => {
  if (!value) return null;
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value));
  if (!match) return null;
  const [,d,m,y] = match;
  const year = Number(y), month = Number(m), day = Number(d);
  const date = new Date(Date.UTC(year, month-1, day));
  if(year < 1900 || year > new Date().getFullYear() ||
    date.getUTCFullYear() !== year || date.getUTCMonth() !== month-1 || date.getUTCDate() !== day) return null;
  return `${y}-${m}-${d}T00:00:00.000Z`;
};
const maskNascimento = value => {
  const digits = String(value || '').replace(/\D/g,'').slice(0,8);
  return [digits.slice(0,2),digits.slice(2,4),digits.slice(4)].filter(Boolean).join('/');
};

/** A API de imagens privadas exige JWT e cabeçalhos de entidade/unidade.
 * Blob revogado quando a ficha troca, fechando vazamentos de memória. */
const useAuthenticatedPhoto = src => {
  const [authenticatedUrl, setAuthenticatedUrl] = React.useState('');
  React.useEffect(() => {
    if (!/^\/api\/beleza\/(?:clientes\/[0-9a-f-]{36}\/foto|fotos\/(?:clientes|colaboradores|servicos|produtos|pacotes|vales-presente)\/[0-9a-f-]{36})\/arquivo(?:\?v=[^#]*)?$/i.test(src || '')) {
      setAuthenticatedUrl(src || ''); return undefined;
    }
    const controller = new AbortController();
    let objectUrl='';
    setAuthenticatedUrl('');
    api.get(src, { responseType:'blob', signal:controller.signal, skipAuthRefresh: true, timeout: 12000 }).then(res => {
      if(controller.signal.aborted) return;
      if (!(res.data instanceof Blob) || !['image/jpeg','image/png','image/webp'].includes(res.data.type)) return;
      objectUrl=URL.createObjectURL(res.data); setAuthenticatedUrl(objectUrl);
    }).catch(() => { if (!controller.signal.aborted) setAuthenticatedUrl(''); });
    return () => { controller.abort(); if(objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);
  return authenticatedUrl;
};
const PhotoPreview = ({src}) => {
  const visualUrl=useAuthenticatedPhoto(src);
  return visualUrl ? <Box component="img" src={visualUrl} alt="Fotografia ampliada"
    sx={{maxWidth:'100%',maxHeight:'70dvh',objectFit:'contain',borderRadius:1}} /> :
    <MuiTypography color="text.secondary">Fotografia indisponível. Atualize a ficha.</MuiTypography>;
};

export const FotoAvatar = ({
  item,
  nome,
  src,
  size = 40,
  variant = "circular",
  sx,
  ...props
}) => {
  const imagem = src || resolveFotoSrc(item);
  const authenticatedSrc = useAuthenticatedPhoto(imagem);
  const texto = nome || getNome(item);

  return (
    <Avatar
      src={authenticatedSrc || undefined}
      variant={variant}
      sx={{
        width: size,
        height: size,
        fontWeight: 800,
        // A miniatura mantém iniciais visíveis se a URL assinada expirar/falhar.
        bgcolor: "primary.main",
        ...sx,
      }}
      {...props}
    >
      {iniciais(texto)}
    </Avatar>
  );
};

export const AvatarFoto = FotoAvatar;
export const ImagemAvatar = FotoAvatar;
export const ImageAvatar = FotoAvatar;

export const FotoMiniatura = ({
  item,
  nome,
  src,
  size = 48,
  sx,
  ...props
}) => (
  <FotoAvatar
    item={item}
    nome={nome}
    src={src}
    size={size}
    variant="rounded"
    sx={{
      borderRadius: 2,
      ...sx,
    }}
    {...props}
  />
);

export const ImagemPreview = ({
  item,
  src,
  alt,
  height = 120,
  sx,
  ...props
}) => {
  const imagem = src || resolveFotoSrc(item);

  if (!imagem) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          bgcolor: "grey.100",
          border: "1px dashed",
          borderColor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          fontSize: 13,
          ...sx,
        }}
        {...props}
      >
        Sem imagem
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={imagem}
      alt={alt || getNome(item) || "Imagem"}
      sx={{
        width: "100%",
        height,
        objectFit: "cover",
        borderRadius: 2,
        display: "block",
        ...sx,
      }}
      {...props}
    />
  );
};

// ============================================================================
// STATUS
// ============================================================================

export const statusColor = (status) => {
  const value = String(status || "").toLowerCase();

  if (
    [
      "confirmado",
      "concluido",
      "concluído",
      "pago",
      "ativo",
      "disponivel",
      "disponível",
      "aberto",
      "aprovado",
      "sucesso",
      "finalizado",
      "sim",
      "aceita",
      "conectado",
    ].includes(value)
  ) {
    return "success";
  }

  if (
    [
      "pendente",
      "aguardandopagamento",
      "aguardando pagamento",
      "aguardando_caixa",
      "aguardando caixa",
      "aguardando",
      "emanalise",
      "em análise",
    ].includes(value)
  ) {
    return "warning";
  }

  if (
    [
      "ematendimento",
      "em atendimento",
      "emexecucao",
      "em execução",
      "em_execucao",
      "iniciado",
      "processando",
      "em andamento",
    ].includes(value)
  ) {
    return "info";
  }

  if (
    [
      "cancelado",
      "cancelada",
      "excluido",
      "excluído",
      "vencido",
      "expirado",
      "erro",
      "falhou",
      "recusado",
      "não",
      "nao",
    ].includes(value)
  ) {
    return "error";
  }

  if (
    [
      "utilizado",
      "baixado",
      "fechado",
      "inativo",
    ].includes(value)
  ) {
    return "secondary";
  }

  return "default";
};

export const agendaStatusLabel = (status) => {
  const value = String(status || "");

  const map = {
    Pendente: "Pendente",
    Confirmado: "Confirmado",
    EmAtendimento: "Em atendimento",
    EmExecucao: "Em execução",
    AguardandoPagamento: "Aguardando caixa",
    Concluido: "Concluído",
    Concluído: "Concluído",
    Pago: "Pago",
    Cancelado: "Cancelado",
    Aberto: "Aberto",
    Finalizado: "Finalizado",
    Ativo: "Ativo",
    Inativo: "Inativo",
    Disponivel: "Disponível",
    Disponível: "Disponível",
    Utilizado: "Utilizado",
    Expirado: "Expirado",
    Vencido: "Vencido",
    Baixado: "Baixado",
    Fechado: "Fechado",
    Sim: "Sim",
    Não: "Não",
    Nao: "Não",
  };

  return map[value] || value || "Não informado";
};

export const statusLabel = agendaStatusLabel;
export const labelStatus = agendaStatusLabel;
export const formatStatus = agendaStatusLabel;

export const StatusChip = ({
  status,
  value,
  label,
  size = "small",
  variant = "filled",
  sx,
  ...props
}) => {
  const raw = status ?? value;
  const texto = label || agendaStatusLabel(raw);

  return (
    <Chip
      size={size}
      variant={variant}
      color={statusColor(raw)}
      label={texto}
      sx={{
        fontWeight: 700,
        ...sx,
      }}
      {...props}
    />
  );
};

// ============================================================================
// LISTAS / NORMALIZAÇÃO
// ============================================================================

export const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.Data)) return data.Data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.Items)) return data.data.Items;
  if (Array.isArray(data?.resultado)) return data.resultado;
  if (Array.isArray(data?.Resultado)) return data.Resultado;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.Rows)) return data.Rows;
  return [];
};

export const normalizarLista = asArray;

export const normalizarId = (value) => {
  if (!value) return "";
  return String(value);
};

export const textoOuTraco = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

export const boolLabel = (value) => (value ? "Sim" : "Não");

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ============================================================================
// RESPONSIVIDADE
// ============================================================================

export const getBelezaPageSize = (width) => {
  const largura =
    typeof width === "number"
      ? width
      : typeof window !== "undefined"
        ? window.innerWidth
        : 1366;

  if (largura <= 480) return 5;
  if (largura <= 768) return 8;
  if (largura <= 1366) return 10;
  if (largura <= 1600) return 15;

  return 20;
};

export const useBelezaPageSize = () => {
  const getSize = () => {
    if (typeof window === "undefined") {
      return { pageSize: 10, dense: true, isCompact: true };
    }

    const pageSize = getBelezaPageSize(window.innerWidth);

    return {
      pageSize,
      dense: window.innerWidth <= 1366,
      isCompact: window.innerWidth <= 1366,
      isMobile: window.innerWidth <= 768,
      isNotebook: window.innerWidth > 1024 && window.innerWidth <= 1366,
    };
  };

  const [state, setState] = React.useState(getSize);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onResize = () => setState(getSize());

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return state;
};

export const useBelezaIsCompact = () => {
  const profile = useBelezaPageSize();
  return profile.isCompact;
};

export const useBelezaDeviceProfile = () => {
  const profile = useBelezaPageSize();

  return {
    width: typeof window !== "undefined" ? window.innerWidth : 1366,
    isMobile: Boolean(profile.isMobile),
    isTablet:
      typeof window !== "undefined" &&
      window.innerWidth > 768 &&
      window.innerWidth <= 1024,
    isNotebook: Boolean(profile.isNotebook),
    isDesktop:
      typeof window !== "undefined" ? window.innerWidth > 1366 : false,
    pageSize: profile.pageSize,
    dense: profile.dense,
  };
};

// ============================================================================
// COMPONENTES DE UI DO MÓDULO
// ============================================================================

export const BelezaPage = ({
  title,
  subtitle,
  actions,
  children,
  sx,
  contentSx,
  ...props
}) => (
  <Box
    className="beleza-page beleza-ui"
    sx={{
      p: { xs: 2, md: 3 },
      width: "100%",
      maxWidth: "1700px",
      mx: "auto",
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 3,
      boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
      overflowX: "hidden",
      ...sx,
    }}
    {...props}
  >
    {(title || subtitle || actions) && (
      <MuiStack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          {title && (
            <MuiTypography variant="h5" fontWeight={750}>
              {title}
            </MuiTypography>
          )}
          {subtitle && (
            <MuiTypography variant="body2" color="text.secondary">
              {subtitle}
            </MuiTypography>
          )}
        </Box>
        {actions && (
          <MuiStack
            direction="row"
            spacing={1}
            justifyContent={{ xs: "stretch", md: "flex-end" }}
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            {actions}
          </MuiStack>
        )}
      </MuiStack>
    )}

    <Box sx={contentSx}>{children}</Box>
  </Box>
);

export const FilterBar = ({ children, onRefresh, actions, sx, ...props }) => (
  <MuiPaper
    elevation={0}
    sx={{
      p: 1.5,
      mb: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      flexWrap: "wrap",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      "& .MuiTextField-root": {
        minWidth: { xs: "100%", sm: 210 },
        flex: "1 1 210px",
        maxWidth: { md: 370 },
      },
      "& .MuiFormControl-root": { minWidth: { xs: "100%", sm: 210 } },
      ...sx,
    }}
    {...props}
  >
    {children}
    <Box sx={{ flex: 1 }} />
    {actions}
    {onRefresh && (
      <MuiButton variant="outlined" onClick={onRefresh}>
        Atualizar
      </MuiButton>
    )}
  </MuiPaper>
);

export const LoadingBox = ({ label = "Carregando...", sx }) => (
  <Box
    sx={{
      minHeight: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 1,
      ...sx,
    }}
  >
    <CircularProgress size={28} />
    <MuiTypography variant="body2" color="text.secondary">
      {label}
    </MuiTypography>
  </Box>
);

export const ResultAlert = ({ error, success, sx }) => {
  if (!error && !success) return null;

  return (
    <MuiStack spacing={1} sx={{ mb: 1.5, ...sx }}>
      {error && (
        <Alert severity="error">
          {typeof error === "string" ? error : error?.message || "Erro inesperado."}
        </Alert>
      )}
      {success && (
        <Alert severity="success">
          {typeof success === "string" ? success : success?.message || "Operação realizada com sucesso."}
        </Alert>
      )}
    </MuiStack>
  );
};

export const StatCard = ({
  label,
  value,
  icon,
  color = "primary",
  helper,
  sx,
}) => (
  <Card
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      height: "100%",
      ...sx,
    }}
  >
    <CardContent>
      <MuiStack direction="row" spacing={1.5} alignItems="center">
        {icon && (
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              color: `${color}.contrastText`,
            }}
          >
            {icon}
          </Avatar>
        )}
        <Box>
          <MuiTypography variant="caption" color="text.secondary">
            {label}
          </MuiTypography>
          <MuiTypography variant="h6" fontWeight={900}>
            {value ?? "-"}
          </MuiTypography>
          {helper && (
            <MuiTypography variant="caption" color="text.secondary">
              {helper}
            </MuiTypography>
          )}
        </Box>
      </MuiStack>
    </CardContent>
  </Card>
);

const getFieldValue = (row, field) => {
  if (!row || !field) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, field)) return row[field];

  const alt = `${field[0]?.toUpperCase() || ""}${field.slice(1)}`;
  if (Object.prototype.hasOwnProperty.call(row, alt)) return row[alt];

  return field.split(".").reduce((acc, key) => acc?.[key], row);
};

export const ResponsiveTable = ({
  rows = [],
  columns = [],
  loading = false,
  dense = false,
  minWidth = 720,
  onEdit,
  onView,
  onDelete,
  emptyText = "Nenhum registro encontrado.",
  sx,
}) => {
  if (loading) {
    return <LoadingBox label="Carregando registros..." />;
  }

  return (
    <>
    <MuiStack spacing={1.25} sx={{ display: { xs: "flex", md: "none" } }}>
      {rows.length === 0 && <MuiPaper variant="outlined" sx={{ p: 3, textAlign: "center" }}><MuiTypography color="text.secondary">{emptyText}</MuiTypography></MuiPaper>}
      {rows.map((row, index) => <MuiPaper key={row?.id || row?.Id || index} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
        <MuiStack spacing={0.8}>
          {columns.map((column, ci) => {
            const value = getFieldValue(row, column.field);
            return <MuiStack key={column.field || ci} direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <MuiTypography variant="caption" color="text.secondary" sx={{ flexShrink: 0, maxWidth: "38%" }}>{column.label}</MuiTypography>
              <Box sx={{ fontWeight: ci === 1 || ci === 0 ? 700 : 400, minWidth: 0, textAlign: "right", overflowWrap: "anywhere" }}>
                {column.render ? column.render(value, row, index) : textoOuTraco(value)}
              </Box>
            </MuiStack>;
          })}
          {(onEdit || onView || onDelete) && <MuiStack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap" sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
            {onView && <MuiButton size="small" variant="text" onClick={() => onView(row)}>Ver</MuiButton>}
            {onEdit && <MuiButton size="small" variant="outlined" onClick={() => onEdit(row)}>Editar</MuiButton>}
            {onDelete && <MuiButton size="small" variant="outlined" color="error" onClick={() => onDelete(row)}>Excluir</MuiButton>}
          </MuiStack>}
        </MuiStack>
      </MuiPaper>)}
    </MuiStack>
    <TableContainer
      component={MuiPaper}
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflowX: "auto",
        display: { xs: "none", md: "block" },
        ...sx,
      }}
    >
      <Table size={dense ? "small" : "medium"} sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field || column.label}
                align={column.align || "left"}
                sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
              >
                {column.label}
              </TableCell>
            ))}
            {(onEdit || onView || onDelete) && (
              <TableCell align="right" sx={{ fontWeight: 900 }}>
                Ações
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + (onEdit || onView || onDelete ? 1 : 0)}>
                <MuiTypography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                  {emptyText}
                </MuiTypography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => {
              const rowId = row?.id || row?.Id || index;

              return (
                <TableRow key={rowId} hover>
                  {columns.map((column) => {
                    const value = getFieldValue(row, column.field);

                    return (
                      <TableCell
                        key={`${rowId}-${column.field || column.label}`}
                        align={column.align || "left"}
                        sx={{
                          maxWidth: column.maxWidth,
                          whiteSpace: column.nowrap ? "nowrap" : undefined,
                        }}
                      >
                        {column.render
                          ? column.render(value, row, index)
                          : textoOuTraco(value)}
                      </TableCell>
                    );
                  })}

                  {(onEdit || onView || onDelete) && (
                    <TableCell align="right">
                      <MuiStack direction="row" spacing={0.8} justifyContent="flex-end">
                        {onView && (
                          <MuiButton size="small" variant="text" onClick={() => onView(row)}>
                            Ver
                          </MuiButton>
                        )}
                        {onEdit && (
                          <MuiButton size="small" variant="outlined" onClick={() => onEdit(row)}>
                            Editar
                          </MuiButton>
                        )}
                        {onDelete && (
                          <MuiButton size="small" color="error" variant="outlined" onClick={() => onDelete(row)}>
                            Excluir
                          </MuiButton>
                        )}
                      </MuiStack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
};

const optionValue = (option) =>
  option?.value ??
  option?.id ??
  option?.Id ??
  option?.setorId ??
  option?.SetorId ??
  "";

const optionLabel = (option) =>
  option?.label ??
  option?.nome ??
  option?.Nome ??
  option?.setorNome ??
  option?.SetorNome ??
  option?.descricao ??
  option?.Descricao ??
  String(option ?? "");

const selectedOption = (options, value) =>
  (options || []).find((opt) => String(optionValue(opt)) === String(value)) || null;


const WEEK_DAYS = [
  { value: 0, letter: "D", label: "Domingo" },
  { value: 1, letter: "S", label: "Segunda" },
  { value: 2, letter: "T", label: "Terça" },
  { value: 3, letter: "Q", label: "Quarta" },
  { value: 4, letter: "Q", label: "Quinta" },
  { value: 5, letter: "S", label: "Sexta" },
  { value: 6, letter: "S", label: "Sábado" },
];

const getAnyFieldValue = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return undefined;
};

const timeText = (value, fallback = "08:00") => {
  if (!value) return fallback;
  const text = String(value);
  if (/^\d{2}:\d{2}/.test(text)) return text.slice(0, 5);
  return fallback;
};

const normalizeAvailabilityItems = (value) => {
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { value = []; }
  }
  return asArray(value).map((item) => ({
    id: getAnyFieldValue(item, "id", "Id") || null,
    diaSemana: Number(getAnyFieldValue(item, "diaSemana", "DiaSemana") ?? 1),
    horaInicio: timeText(getAnyFieldValue(item, "horaInicio", "HoraInicio"), "08:00"),
    horaFim: timeText(getAnyFieldValue(item, "horaFim", "HoraFim"), "18:00"),
    intervaloInicio: getAnyFieldValue(item, "intervaloInicio", "IntervaloInicio") ? timeText(getAnyFieldValue(item, "intervaloInicio", "IntervaloInicio"), "12:00") : null,
    intervaloFim: getAnyFieldValue(item, "intervaloFim", "IntervaloFim") ? timeText(getAnyFieldValue(item, "intervaloFim", "IntervaloFim"), "13:00") : null,
    ativo: getAnyFieldValue(item, "ativo", "Ativo") !== false,
  })).filter((item) => item.ativo);
};

const AvailabilityWeekField = ({ label, value, helperText, onChange, readOnly = false }) => {
  const items = normalizeAvailabilityItems(value);
  const error = validarGradeSemanal(items);
  const update = (next) => { if (!readOnly) onChange(next.map((item) => ({ ...item, ativo: true }))); };
  const addInterval = (diaSemana) => {
    const existing = items.filter((x) => Number(x.diaSemana) === Number(diaSemana));
    const last = existing[existing.length - 1];
    update([...items, { id: null, diaSemana, horaInicio: last?.horaFim || "08:00",
      horaFim: "18:00", intervaloInicio: null, intervaloFim: null, ativo: true }]);
  };
  const removeInterval = (diaSemana, indexInDay) => {
    let seen = -1;
    update(items.filter((item) => Number(item.diaSemana) !== Number(diaSemana) || ++seen !== indexInDay));
  };
  const changeInterval = (diaSemana, indexInDay, fieldName, fieldValue) => {
    let seen = -1;
    update(items.map((item) => {
      if (Number(item.diaSemana) !== Number(diaSemana) || ++seen !== indexInDay) return item;
      return { ...item, [fieldName]: fieldValue || null };
    }));
  };
  const copyFirstDayToWeekdays = () => {
    const sourceDay = [1,2,3,4,5].find(day => items.some(item => Number(item.diaSemana) === day));
    if (!sourceDay || !window.confirm("Substituir os horários de segunda a sexta pelos horários do primeiro dia útil configurado?")) return;
    const source = items.filter(item => Number(item.diaSemana) === sourceDay);
    const weekends = items.filter(item => [0,6].includes(Number(item.diaSemana)));
    update([...weekends, ...[1,2,3,4,5].flatMap(day => source.map(item => ({ ...item, id: null, diaSemana: day })))]);
  };
  return (
    <MuiStack spacing={1.5} className="beleza-week">
      <MuiStack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
        <Box>
          <MuiTypography fontWeight={750}>{label || "Horários semanais"}</MuiTypography>
          <MuiTypography variant="body2" color="text.secondary">A agenda utiliza esta grade para encontrar horários e encaixes disponíveis.</MuiTypography>
        </Box>
        {!readOnly && <MuiButton variant="outlined" size="small" onClick={copyFirstDayToWeekdays}
          disabled={!items.some(item => [1,2,3,4,5].includes(Number(item.diaSemana)))}>Copiar para dias úteis</MuiButton>}
      </MuiStack>
      <MuiStack spacing={1}>
        {WEEK_DAYS.map(day => {
          const dayItems = items.filter(item => Number(item.diaSemana) === day.value);
          return (
            <MuiPaper key={day.value} variant="outlined" className="beleza-week-day" sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
              <MuiStack direction={{ xs: "column", md: "row" }} gap={1.5} alignItems={{ md: "flex-start" }}>
                <Box sx={{ flex: "0 0 112px", pt: 0.5 }}>
                  <MuiTypography fontWeight={700}>{day.label}</MuiTypography>
                  <MuiTypography variant="caption" color="text.secondary">{dayItems.length ? `${dayItems.length} turno(s)` : "Folga"}</MuiTypography>
                </Box>
                <MuiStack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  {dayItems.map((item, index) => (
                    <Box key={`${day.value}-${index}`} className="beleza-week-interval">
                      {[
                        { key: "horaInicio", label: "Entrada" },
                        { key: "horaFim", label: "Saída" },
                        { key: "intervaloInicio", label: "Início da pausa" },
                        { key: "intervaloFim", label: "Fim da pausa" },
                      ].map(timeField => <MuiTextField key={timeField.key} label={timeField.label} type="time" size="small"
                        value={item[timeField.key] || ""} disabled={readOnly} InputLabelProps={{ shrink: true }}
                        onChange={event => changeInterval(day.value, index, timeField.key, event.target.value)} />)}
                      {!readOnly && <MuiButton variant="outlined" size="small" color="error" onClick={() => removeInterval(day.value, index)}>Remover</MuiButton>}
                    </Box>
                  ))}
                  {!readOnly && <Box><MuiButton variant="text" size="small" onClick={() => addInterval(day.value)}>
                    {dayItems.length ? "+ Adicionar outro turno" : "+ Configurar horário"}
                  </MuiButton></Box>}
                </MuiStack>
              </MuiStack>
            </MuiPaper>
          );
        })}
      </MuiStack>
      {error && <Alert severity="warning" role="alert">{error}</Alert>}
      <MuiTypography variant="caption" color="text.secondary">
        {helperText || "As pausas são opcionais. Turnos sobrepostos não podem ser gravados."}
      </MuiTypography>
    </MuiStack>
  );
};

const SALON_COLORS = ["#1976d2", "#0f766e", "#7c3aed", "#db2777", "#e67e22", "#b91c1c", "#374151", "#0891b2"];
const SalonColorField = ({ field, value, readOnly, onChange, error }) => (
  <MuiStack spacing={1.25} className="beleza-color-field">
    <MuiTypography fontWeight={700} component="label">{field.label}</MuiTypography>
    <MuiStack direction="row" gap={1} alignItems="center" flexWrap="wrap">
      {SALON_COLORS.map(color => <MuiButton key={color} type="button" variant="outlined" disabled={readOnly}
        aria-label={`Escolher cor ${color}`} aria-pressed={String(value).toLowerCase() === color}
        onClick={() => onChange(color)} className="beleza-color-swatch"
        sx={{ minWidth: 40, width: 40, height: 40, borderRadius: "50%", bgcolor: color,
          border: String(value).toLowerCase() === color ? "3px solid" : "2px solid", borderColor: String(value).toLowerCase() === color ? "primary.main" : "divider",
          "&:hover": { bgcolor: color } }} />)}
      <MuiTextField label="Cor personalizada" type="color" size="small" disabled={readOnly} value={/^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#1976d2"}
        onChange={event => onChange(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ maxWidth: 180 }} />
    </MuiStack>
    <MuiStack direction="row" gap={1} alignItems="center">
      <Box aria-hidden="true" sx={{ bgcolor: value || "#1976d2", width: 28, height: 28, borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
      <MuiTypography variant="body2" color="text.secondary">Pré-visualização na agenda: {value || "#1976d2"}</MuiTypography>
    </MuiStack>
    {error && <MuiTypography role="alert" color="error" variant="caption">{error}</MuiTypography>}
  </MuiStack>
);

/**
 * Editor reutilizável de imagens, isolado da lógica da ficha.
 * A escolha de um ficheiro NÃO usa <label> para evitar cliques propagados e
 * re-navegações; todas as ações são botões com type="button".
 */
export const SalonPhotoPicker = ({ field, value, display, nome, disabled, error,
  onFile, onRemove, onCamera, onPreview }) => {
  const inputRef = React.useRef(null);
  const [menuEl, setMenuEl] = React.useState(null);
  const closeMenu = () => setMenuEl(null);
  return <Box className="beleza-photo-picker">
    <MuiTypography variant="subtitle2" className="beleza-photo-title">{field.label}</MuiTypography>
    <Box className="beleza-photo-stage">
      {display ? <FotoAvatar src={display} nome={nome} size={116} variant="rounded"
        sx={{ width:'100%',height:'100%',borderRadius:'inherit',bgcolor:'#eaf0f7',color:'#335c91',fontSize:28,
          '& .MuiAvatar-img': { objectFit:'cover' } }}/> :
        <Box className="beleza-photo-placeholder" role="img" aria-label="Sem fotografia"><AddAPhotoOutlined/></Box>}
      {display && <Box className="beleza-photo-overlay" aria-label="Ações da fotografia">
        {display && <Tooltip title="Visualizar foto"><span><IconButton type="button" size="small"
          onClick={() => onPreview(display)} aria-label="Visualizar fotografia"><VisibilityOutlined fontSize="small"/></IconButton></span></Tooltip>}
        {!disabled && <Tooltip title={display ? 'Substituir foto' : 'Adicionar foto'}><span><IconButton type="button" size="small"
          onClick={event => setMenuEl(event.currentTarget)} aria-label="Editar fotografia" aria-haspopup="menu"
          aria-expanded={Boolean(menuEl)}><EditOutlined fontSize="small"/></IconButton></span></Tooltip>}
        {!disabled && Boolean(display) && <Tooltip title="Remover foto"><span><IconButton type="button" size="small"
          onClick={onRemove} aria-label="Remover fotografia"><DeleteOutline fontSize="small"/></IconButton></span></Tooltip>}
      </Box>}
      {!display && !disabled && <IconButton type="button" className="beleza-photo-empty-action"
        aria-label="Adicionar fotografia" onClick={event => setMenuEl(event.currentTarget)} sx={{width:'100%',height:'100%'}} />}
    </Box>
    <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
      aria-label="Selecionar fotografia" onClick={event => event.stopPropagation()}
      onCancel={photoPickerClosed}
      onChange={event => {
        photoPickerClosed();
        event.stopPropagation();
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) onFile(file);
      }} />
    <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu} keepMounted
      anchorOrigin={{ vertical:'bottom', horizontal:'right' }} transformOrigin={{vertical:'top',horizontal:'right'}}>
      <MenuItem onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        photoPickerOpened();
        inputRef.current?.click();
      }}>
        <AddAPhotoOutlined fontSize="small" sx={{mr:1}}/> Escolher do dispositivo
      </MenuItem>
      {field.enableCamera && <MenuItem onClick={() => { closeMenu(); onCamera(); }}>
        <CameraAltOutlined fontSize="small" sx={{mr:1}}/> Tirar com webcam
      </MenuItem>}
    </Menu>
    <MuiTypography variant="caption" className="beleza-photo-help">JPG, PNG ou WebP · 8 MB</MuiTypography>
    {error && <MuiTypography color="error" role="alert" variant="caption">{error}</MuiTypography>}
  </Box>;
};

export const FormDialog = ({
  open,
  title,
  values = {},
  fields = [],
  saving = false,
  maxWidth = "md",
  onClose,
  onChange,
  onSubmit,
  readOnly = false,
  layoutVariant = "default",
  submitError = "",
  extraContent = null,
  extraTabLabel = "Histórico",
  extraTabs = [],
}) => {
  const [extraTab, setExtraTab] = React.useState(0);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [cameraField, setCameraField] = React.useState("");
  const [cameraError, setCameraError] = React.useState("");
  const [previewSrc, setPreviewSrc] = React.useState("");
  const videoRef = React.useRef(null);
  const cameraStreamRef = React.useRef(null);
  const lastPostalQuery = React.useRef('');
  React.useEffect(() => { if (open) { setFieldErrors({}); setExtraTab(0); } else { setCameraField(""); setPreviewSrc(""); } }, [open]);
  React.useEffect(() => {
    let disposed = false;
    const stop = () => {
      cameraStreamRef.current?.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
    if (!cameraField || !open) { stop(); return stop; }
    setCameraError("");
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCameraError("A webcam exige HTTPS (ou localhost) e permissão do navegador.");
      return stop;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then(stream => {
        if (disposed) { stream.getTracks().forEach(track => track.stop()); return; }
        cameraStreamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play()?.catch(() => {}); }
      })
      .catch(() => { if (!disposed) setCameraError("Não foi possível acessar a câmera. Verifique as permissões do navegador."); });
    return () => { disposed = true; stop(); };
  }, [cameraField, open]);
  const captureCameraPhoto = (field) => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError("A câmera ainda não disponibilizou imagem."); return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = Math.min(video.videoWidth, 1600);
    canvas.height = Math.round(canvas.width * video.videoHeight / video.videoWidth);
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) { setCameraError("Falha ao capturar imagem."); return; }
      const file = new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" });
      const last = values?.[field.name];
      if (typeof last === "string" && last.startsWith("blob:")) URL.revokeObjectURL(last);
      onChange?.("__fotoFile", file);
      onChange?.("__fotoRemove", false);
      onChange?.(field.name, URL.createObjectURL(file));
      setCameraField("");
    }, "image/jpeg", 0.86);
  };
  React.useEffect(() => () => {
    const pending = values?.fotoUrl;
    if (typeof pending === 'string' && pending.startsWith('blob:')) URL.revokeObjectURL(pending);
  }, [values?.fotoUrl]);
  const validate = () => {
    const next = {};
    for (const field of fields.filter(item => !item.visibleWhen || item.visibleWhen(values))) {
      const v = values?.[field.name];
      if (field.required && (v === undefined || v === null || String(v).trim() === '' || (Array.isArray(v) && !v.length))) next[field.name] = 'Campo obrigatório';
      if ((field.name === 'email' || field.name === 'Email') && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) next[field.name] = 'E-mail inválido';
      if ((/^(whatsApp|telefone)$/i.test(field.name)) && v && !/^\+?[0-9\s().-]{8,24}$/.test(String(v).trim())) next[field.name] = 'Informe telefone válido com DDI/DDD';
      if (field.name === 'cpf' && v && /^(brasil|brazil|br)$/i.test(values?.pais||'Brasil') && !/^\d{11}$/.test(String(v).replace(/\D/g, ''))) next[field.name] = 'CPF deve conter 11 dígitos';
      if (field.type === 'money' && v !== undefined && v !== '' &&
          (!/^\s*(?:R\$|€|\$)?\s*-?\d[\d.,]*\s*$/.test(String(v)) ||
            !Number.isFinite(moedaParaNumero(v)) || moedaParaNumero(v) < 0)) next[field.name] = 'Valor monetário inválido';
      if (field.type === 'date' && field.name === 'dataNascimento' && v && !nascimentoParaIso(v))
        next[field.name] = 'Informe uma data válida no formato dd/mm/aaaa';
      if (field.type === 'date' && field.name !== 'dataNascimento' && v &&
          (!/^\d{4}-\d{2}-\d{2}$/.test(String(v)) || Number.isNaN(Date.parse(String(v)))))
        next[field.name] = 'Data inválida';
      if (field.type === 'number' && v !== '' && v != null && (!Number.isFinite(Number(v)) || Number(v) < 0)) next[field.name] = 'Valor deve ser positivo';
      if (field.type === 'availability-week') {
        const gradeError = validarGradeSemanal(v || []);
        if (gradeError) next[field.name] = gradeError;
      }
    }
    setFieldErrors(next);
    return !Object.keys(next).length;
  };
  const handleFieldChange = (field, value) => {
    setFieldErrors((prev) => ({ ...prev, [field.name]: undefined }));
    if (onChange && !readOnly) onChange(field.name, value);
  };

  const renderField = (field) => {
    const value = values?.[field.name];

    if (field.type === "color") {
      return <SalonColorField field={field} value={value} readOnly={readOnly}
        error={fieldErrors[field.name]} onChange={(color) => handleFieldChange(field, color)} />;
    }

    if (field.type === "switch") {
      return (
        <FormControlLabel
          control={
            <Switch
              disabled={readOnly}
              checked={Boolean(value)}
              onChange={(event) => handleFieldChange(field, event.target.checked)}
            />
          }
          label={field.label}
        />
      );
    }

    if (field.type === "select") {
      return (
        <MuiTextField
          fullWidth
          select
          size="medium"
          label={field.label}
          value={value ?? ""}
          required={field.required}
          SelectProps={{ displayEmpty: false, MenuProps: { PaperProps: { sx: { maxHeight: 340, maxWidth: 'min(96vw, 520px)' } } } }}
          helperText={fieldErrors[field.name] || field.helperText}
          error={Boolean(fieldErrors[field.name])}
          disabled={readOnly}
          onChange={(event) => handleFieldChange(field, event.target.value)}
        >
          <MenuItem value="" disabled>Selecione uma opção</MenuItem>
          {(field.options || []).map((option) => (
            <MenuItem key={optionValue(option)} value={optionValue(option)}>
              {optionLabel(option)}
            </MenuItem>
          ))}
        </MuiTextField>
      );
    }

    if (field.type === "autocomplete") {
      const options = field.options || [];

      return (
        <Autocomplete
          multiple={Boolean(field.multiple)}
          options={options}
          value={
            field.multiple
              ? options.filter((opt) => (value || []).map(String).includes(String(optionValue(opt))))
              : selectedOption(options, value)
          }
          getOptionLabel={(option) => optionLabel(option)}
          isOptionEqualToValue={(option, selected) =>
            String(optionValue(option)) === String(optionValue(selected))
          }
          disabled={readOnly}
          onChange={(event, selected) => {
            if (field.multiple) {
              handleFieldChange(field, (selected || []).map(optionValue));
            } else {
              handleFieldChange(field, selected ? optionValue(selected) : "");
            }
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={field.label}
              required={field.required}
              helperText={fieldErrors[field.name] || field.helperText}
              error={Boolean(fieldErrors[field.name])}
              size="medium"
              sx={{ "& .MuiInputBase-root": { minHeight: 52 } }}
            />
          )}
        />
      );
    }

    if (field.type === "photo-upload") {
      const display = values?.__fotoRemove ? "" :
        (typeof value === "string" && value.startsWith("blob:"))
          ? value : (values?.__fotoDisplayUrl || resolveFotoSrc(value));
      return <SalonPhotoPicker
        field={field} value={value} display={display}
        nome={values?.nome || values?.Nome || 'Registo'} disabled={readOnly || saving}
        error={fieldErrors[field.name]} onPreview={setPreviewSrc}
        onCamera={() => { setCameraError(''); setCameraField(field.name); }}
        onFile={file => {
          if (!['image/png','image/jpeg','image/webp'].includes(file.type) || file.size > 8*1024*1024 || !file.size) {
            setFieldErrors(prev => ({...prev,[field.name]:'Envie JPG, PNG ou WebP até 8 MB.'}));
            return;
          }
          if (typeof value === 'string' && value.startsWith('blob:')) URL.revokeObjectURL(value);
          handleFieldChange({name:'__fotoFile'},file);
          handleFieldChange({name:'__fotoRemove'},false);
          handleFieldChange(field,URL.createObjectURL(file));
        }}
        onRemove={() => {
          if (typeof value === 'string' && value.startsWith('blob:')) URL.revokeObjectURL(value);
          handleFieldChange({name:'__fotoFile'},null);
          handleFieldChange({name:'__fotoRemove'},true);
          handleFieldChange({name:'__fotoDisplayUrl'},'');
          handleFieldChange(field,'');
        }}/>
    }

    if (field.type === "availability-week") {
      return (
        <AvailabilityWeekField
          label={field.label}
          helperText={field.helperText}
          value={value}
          onChange={(next) => handleFieldChange(field, next)}
          readOnly={readOnly}
        />
      );
    }

    if (field.type === "package-services") {
      const selections = Array.isArray(value) ? value : [];
      return (
        <MuiStack spacing={1.5} sx={{ width: "100%" }}>
          <MuiTypography fontWeight={700}>{field.label}</MuiTypography>
          {selections.map((entry, index) => (
            <MuiStack key={`${entry.servicoId || index}-${index}`} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <Autocomplete size="small" disabled={readOnly} fullWidth options={field.options || []}
                value={selectedOption(field.options || [], entry.servicoId || entry.ServicoId) || null}
                getOptionLabel={(o) => optionLabel(o)} isOptionEqualToValue={(a,b)=>String(optionValue(a))===String(optionValue(b))}
                onChange={(_, opt) => handleFieldChange(field, selections.map((v,i) => i === index ? {...v, servicoId: optionValue(opt)} : v))}
                renderInput={(params)=><MuiTextField {...params} label="Serviço" />}/>
              <MuiTextField size="small" disabled={readOnly} label="Quantidade" type="number" sx={{ minWidth: { sm: 130 }, width: { xs: "100%", sm: 140 } }}
                inputProps={{ min: 1, step: 1 }} value={entry.quantidadePermitida ?? 1}
                onChange={(e) => handleFieldChange(field, selections.map((v,i)=>i===index?{...v, quantidadePermitida: Math.max(1,Number(e.target.value)||1)}:v))}/>
              <MuiButton color="error" disabled={readOnly} onClick={() => handleFieldChange(field,selections.filter((_,i)=>i!==index))}>Remover</MuiButton>
            </MuiStack>
          ))}
          {!readOnly && <MuiButton variant="outlined" onClick={() => handleFieldChange(field,[...selections,{servicoId:"",quantidadePermitida:1}])}>Adicionar serviço</MuiButton>}
        </MuiStack>
      );
    }

    if (field.type === 'date' && field.name === 'dataNascimento') {
      return <MuiTextField fullWidth size="small" type="text" label={field.label}
        inputProps={{inputMode:'numeric',maxLength:10,placeholder:'dd/mm/aaaa'}}
        InputLabelProps={{shrink:true}}
        autoComplete="bday" value={nascimentoParaTexto(value)}
        disabled={readOnly} required={field.required}
        error={Boolean(fieldErrors[field.name])}
        helperText={fieldErrors[field.name] || field.helperText}
        onChange={event => handleFieldChange(field,maskNascimento(event.target.value))}/>;
    }

    if (field.type === 'postal') {
      return <MuiTextField fullWidth size="small" label={field.label}
        value={value ?? ''} disabled={readOnly}
        helperText={fieldErrors[field.name] || field.helperText}
        error={Boolean(fieldErrors[field.name])}
        InputProps={{endAdornment: (!readOnly && field.lookup) ? <MuiButton
          type="button" size="small" disabled={Boolean(values.__postalLoading)}
          onClick={() => field.lookup(values,onChange)} sx={{minWidth:72,px:1}}>Consultar</MuiButton> : null}}
        onChange={e => {lastPostalQuery.current=''; handleFieldChange(field,e.target.value);}}
        onBlur={() => {
          if (readOnly || !field.lookup || values.__postalLoading) return;
          const country=String(values.pais||'Brasil').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();
          const postal=String(value||'').trim().toUpperCase();
          const valid=/(brasil|brazil|^br$)/.test(country) ? /^\d{8}$/.test(postal.replace(/\D/g,'')) :
            /(portugal|^pt$)/.test(country) ? /^\d{4}-\d{3}$/.test(postal) :
            /(estados unidos|united states|usa|^us$)/.test(country) ? /^\d{5}(-\d{4})?$/.test(postal) : false;
          const key=`${country}:${postal}`;
          if (valid && lastPostalQuery.current!==key) {
            lastPostalQuery.current=key;
            field.lookup(values,onChange);
          }
        }}/>;
    }

    return (
      <MuiTextField
        fullWidth
        size="small"
        type={
          field.type === "money"
            ? "text"
            : field.type === "date"
              ? "date"
              : field.type === "color"
                ? "color"
                : field.type || "text"
        }
        label={field.label}
        value={value ?? ""}
        required={field.required}
        multiline={Boolean(field.multiline)}
        minRows={field.multiline ? 2 : undefined}
        autoComplete={field.name === 'email' ? 'email' : /telefone|whatsApp/i.test(field.name) ? 'tel' : 'off'}
        helperText={fieldErrors[field.name] || field.helperText}
        error={Boolean(fieldErrors[field.name])}
        disabled={readOnly}
        inputProps={field.type === "number" ? { min: 0, step: 1 } : field.type === 'money' ? { inputMode: 'decimal' } : /telefone|whatsApp/i.test(field.name) ? { inputMode: 'tel' } : undefined}
        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
        onChange={(event) => handleFieldChange(field, event.target.value)}
      />
    );
  };

  const visibleFields = fields.filter(field => !field.visibleWhen || field.visibleWhen(values));
  const headerGroups = {
    colaborador: ["fotoUrl", "nome", "setorIds", "ativo"],
    cliente: ["fotoUrl", "nome", "cpf", "ativo", "whatsApp", "telefone", "email", "dataNascimento", "genero"],
    voucher: ["fotoUrl", "nome", "tipoBeneficio", "ativo"],
    produto: ["fotoUrl", "nome", "categoriaId", "ativo"],
    pacote: ["fotoUrl", "nome", "tipo", "ativo"],
  };
  const headerFieldNames = new Set(headerGroups[layoutVariant] || []);
  const headerFields = visibleFields.filter(field => headerFieldNames.has(field.name));
  const grouped = visibleFields.filter(field => !headerFieldNames.has(field.name)).reduce((sections, field) => {
    const sectionName = field.section || "Dados do cadastro";
    const existing = sections.find(section => section.title === sectionName);
    if (existing) existing.fields.push(field);
    else sections.push({ title: sectionName, fields: [field] });
    return sections;
  }, []);
  const renderGridField = (field) => <MuiGrid item xs={12} sm={field.sm === 12 ? 12 : 6}
    md={field.md || field.sm || 6} key={field.name} sx={{ minWidth: 0 }}>{renderField(field)}</MuiGrid>;
  const clientHeaderNames = ['nome','whatsApp','email','telefone','cpf','dataNascimento','genero'];

  return (
    <>
    <Dialog open={Boolean(open)} onClose={saving ? undefined : (event, reason) => {
      // O seletor nativo de ficheiros e os menus em portal não devem fechar a ficha.
      if (reason === "backdropClick") return;
      onClose?.();
    }} fullWidth maxWidth={maxWidth}
      className="beleza-ui-dialog beleza-form-dialog" fullScreen={typeof window !== "undefined" && window.innerWidth <= 600}
      PaperProps={{ sx: { maxHeight: { xs: "100dvh", sm: "calc(100dvh - 32px)" }, maxWidth: "min(100% - 24px, 1024px)" } }}>
      <DialogTitle sx={{ fontWeight: 750 }}>
        {title}
        <MuiTypography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {readOnly ? "Visualização de informações do registo" : "Preencha os campos obrigatórios e guarde as alterações."}
        </MuiTypography>
      </DialogTitle>
      <DialogContent dividers>
        <MuiStack spacing={2} sx={{ pt: 0.5 }}>
          {(extraContent || extraTabs.length > 0) && <Tabs value={extraTab}
              onChange={(_, index) => { setExtraTab(index); if(index) setCameraField(""); }}
              aria-label="Secções do registo" variant="scrollable" scrollButtons="auto">
            <Tab label="Dados do cadastro"/>
            {extraTabs.length ? extraTabs.map(tab => <Tab key={tab.label} label={tab.label} disabled={Boolean(tab.disabled)}/>) :
              <Tab label={extraTabLabel}/>}
          </Tabs>}
          {submitError && <Alert severity="error" role="alert">{submitError}</Alert>}
          {extraTab === 0 && headerFields.length > 0 && <Box className="beleza-staff-profile">
            {layoutVariant === 'cliente' ? <Box className="beleza-client-fields">
              {clientHeaderNames.map(name => headerFields.find(field => field.name === name)).filter(Boolean).map(field =>
                <Box key={field.name} className={`beleza-client-field beleza-client-field--${field.name}`}>{renderField(field)}</Box>)}
            </Box> : <MuiGrid container spacing={1.5} alignItems="flex-start" className="beleza-form-grid beleza-profile-fields">
              {headerFields.filter(field => !['fotoUrl'].includes(field.name)).map(renderGridField)}
            </MuiGrid>}
            <Box className="beleza-staff-profile-photo">
              {headerFields.filter(field => field.name === 'fotoUrl').map(field => <Box key={field.name}>{renderField(field)}</Box>)}
              {layoutVariant === 'cliente' && headerFields.filter(field => field.name === 'ativo').map(field =>
                <Box className="beleza-photo-active" key={field.name}>{renderField(field)}</Box>)}
            </Box>
          </Box>}
          {extraTab === 0 && grouped.map(section => <Box component="section" className="beleza-form-section" key={section.title}>
            <MuiTypography variant="subtitle1" fontWeight={750} className="beleza-form-section-title">{section.title}</MuiTypography>
            <MuiGrid container spacing={2} alignItems="flex-start" className="beleza-form-grid">
              {section.fields.map(renderGridField)}
            </MuiGrid>
          </Box>)}
          {extraTab > 0 && (extraTabs.length ? extraTabs[extraTab-1]?.content : extraContent)}
          {extraTab === 0 && Object.values(fieldErrors).some(Boolean) && <Alert severity="warning" role="alert">Revise os campos assinalados antes de guardar.</Alert>}
        </MuiStack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={saving}>{readOnly ? "Fechar" : "Cancelar"}</MuiButton>
        {!readOnly && extraTab === 0 && <MuiButton variant="contained" onClick={() => { if (validate()) onSubmit?.(values); }} disabled={saving}>
          {saving ? "A guardar…" : ((values?.id || values?.Id) ? "Guardar alterações" : "Criar registo")}
        </MuiButton>}
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(previewSrc) && Boolean(open)} onClose={() => setPreviewSrc('')} maxWidth="md" fullWidth
      aria-labelledby="beleza-photo-view-title">
      <DialogTitle id="beleza-photo-view-title">Fotografia</DialogTitle>
      <DialogContent dividers sx={{textAlign:'center'}}>
        {previewSrc && <PhotoPreview src={previewSrc}/>}
      </DialogContent>
      <DialogActions><MuiButton type="button" onClick={() => setPreviewSrc('')}>Fechar</MuiButton></DialogActions>
    </Dialog>
    <Dialog open={Boolean(cameraField) && Boolean(open)} onClose={() => setCameraField('')} maxWidth="sm" fullWidth
      aria-labelledby="beleza-camera-dialog-title">
      <DialogTitle id="beleza-camera-dialog-title">Capturar fotografia</DialogTitle>
      <DialogContent dividers>
        <MuiTypography variant="body2" color="text.secondary" sx={{mb:1.5}}>
          Autorize a webcam no navegador. A imagem só será enviada quando guardar o cadastro.
        </MuiTypography>
        <Box component="video" ref={videoRef} autoPlay muted playsInline
          sx={{width:'100%',maxHeight:360,objectFit:'contain',borderRadius:2,bgcolor:'#182331'}} />
        {cameraError && <Alert severity="warning" sx={{mt:1}}>{cameraError}</Alert>}
      </DialogContent>
      <DialogActions>
        <MuiButton type="button" variant="outlined" onClick={() => setCameraField('')}>Cancelar</MuiButton>
        <MuiButton type="button" variant="contained" disabled={Boolean(cameraError) || readOnly || saving}
          onClick={() => captureCameraPhoto({name:cameraField})}>Capturar</MuiButton>
      </DialogActions>
    </Dialog>
    </>
  );
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const belezaShared = {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,

  BRL,
  formatMoney,
  moeda,
  money,
  formatCurrency,
  formatarMoeda,
  valorMonetario,
  moedaParaNumero,
  parseMoeda,
  currencyToNumber,
  moneyToNumber,
  valorParaNumero,
  numeroParaMoedaInput,
  formatarCampoMoeda,
  calcularSubtotal,
  porcentagem,
  clamp,

  formatDateInput,
  formatDateTimePtBr,
  formatDatePtBr,
  dataPtBr,
  dataHoraPtBr,
  dataHora,
  formatarData,
  formatarDataHora,

  resolveFotoSrc,
  resolveImagemSrc,
  resolveImageSrc,
  getImagem,
  getFoto,
  getFotoUrl,
  getNome,
  iniciais,
  FotoAvatar,
  AvatarFoto,
  ImagemAvatar,
  ImageAvatar,
  FotoMiniatura,
  ImagemPreview,

  statusColor,
  agendaStatusLabel,
  statusLabel,
  labelStatus,
  formatStatus,
  StatusChip,

  asArray,
  normalizarLista,
  normalizarId,
  textoOuTraco,
  boolLabel,
  sleep,

  getBelezaPageSize,
  useBelezaPageSize,
  useBelezaIsCompact,
  useBelezaDeviceProfile,

  BelezaPage,
  FilterBar,
  LoadingBox,
  ResultAlert,
  ResponsiveTable,
  StatCard,
  FormDialog,
};

export default belezaShared;